import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Microphone, Copy, CaretDown, CheckCircle, User } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function VoiceNoteContractor({ jobId, onCopyToInvoice }) {
  const [voiceNote, setVoiceNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    fetchLatestVoiceNote();
  }, [jobId]);

  const fetchLatestVoiceNote = async () => {
    try {
      const res = await fetch(
        `${API}/contractor/jobs/${jobId}/voice-notes/latest`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setVoiceNote(data.voice_note);
      }
    } catch (err) {
      console.error("Error fetching voice note:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToInvoice = () => {
    if (!voiceNote?.ai_summary) return;
    
    // Try to use onCopyToInvoice callback if provided
    if (onCopyToInvoice) {
      onCopyToInvoice(voiceNote.ai_summary);
      toast.success("Summary copied to invoice notes");
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(voiceNote.ai_summary);
      toast.success("Summary copied to clipboard");
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!voiceNote) {
    return null; // Don't show card if no voice notes
  }

  return (
    <Card className="border-2 border-zinc-200" data-testid="voice-note-contractor-card">
      <CardHeader className="border-b border-zinc-100 py-4">
        <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
          <Microphone size={20} />
          Technician Voice Note
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Technician info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <User size={16} />
            <span>{voiceNote.technician_name || "Technician"}</span>
          </div>
          <span className="text-xs text-zinc-400">
            {formatTimestamp(voiceNote.created_at)}
          </span>
        </div>

        {/* AI Summary */}
        {voiceNote.ai_summary && (
          <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
            <div className="flex items-center justify-between mb-2">
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle size={14} className="mr-1" />
                AI Summary
              </Badge>
            </div>
            <div className="text-sm whitespace-pre-wrap text-zinc-700">
              {voiceNote.ai_summary}
            </div>
          </div>
        )}

        {/* Transcript (collapsible) */}
        {voiceNote.raw_transcript && (
          <div>
            <button
              onClick={() => setShowTranscript(!showTranscript)}
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700"
            >
              <CaretDown
                size={14}
                className={`transition-transform ${showTranscript ? "rotate-180" : ""}`}
              />
              {showTranscript ? "Hide" : "View"} Full Transcript
            </button>
            {showTranscript && (
              <div className="mt-2 p-3 bg-white border border-zinc-200 rounded text-sm text-zinc-600 max-h-48 overflow-y-auto">
                {voiceNote.raw_transcript}
              </div>
            )}
          </div>
        )}

        {/* Copy to Invoice Button */}
        {voiceNote.ai_summary && (
          <Button
            variant="outline"
            onClick={copyToInvoice}
            className="w-full border-2 gap-2"
            data-testid="copy-to-invoice-btn"
          >
            <Copy size={16} />
            Copy Summary to Invoice Notes
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default VoiceNoteContractor;
